<template>
    <!-- Плитки: факт к норме, нарушения, биржа -->
    <div class="ds-dossier-tiles">
        <div v-if="statsState.status === 'loading'" class="ds-dossier-skeleton" style="height: 86px" aria-busy="true"></div>
        <div v-else class="ds-dossier-tile">
            <span class="ds-dossier-tile-label">Факт / норма</span>
            <span class="ds-dossier-tile-value" :class="{ 'is-muted': factPercent === null }">{{ factPercent === null ? DASH : `${factPercent} %` }}</span>
            <span v-if="statsState.status === 'forbidden'" class="ds-dossier-tile-note">Недоступно с текущими правами</span>
            <span v-else-if="statsState.status === 'error'" class="ds-dossier-tile-note">
                Ошибка · <button type="button" class="ds-dossier-link" @click="retryStats">Повторить</button>
            </span>
            <span v-else class="ds-dossier-tile-note">{{ formatHours(metrics.fact) }} из {{ formatHours(metrics.norm) }} ч</span>
        </div>

        <!-- Нарушения месяца: discipline.totals по всем доступным ТУ, включая прогулы. -->
        <div v-if="disciplineState.status === 'loading'" class="ds-dossier-skeleton" style="height: 86px" aria-busy="true"></div>
        <div v-else class="ds-dossier-tile" :class="{ 'is-violation': (violationsCount ?? 0) > 0 }" title="По всем доступным трудоустройствам">
            <span class="ds-dossier-tile-label">Нарушения</span>
            <span class="ds-dossier-tile-value" :class="{ 'is-muted': !violationsCount }">{{ violationsCount ?? DASH }}</span>
            <span v-if="disciplineState.status === 'forbidden'" class="ds-dossier-tile-note">Недоступно с текущими правами</span>
            <span v-else-if="disciplineState.status === 'error'" class="ds-dossier-tile-note">
                Ошибка · <button type="button" class="ds-dossier-link" @click="retryDiscipline">Повторить</button>
            </span>
            <span v-else-if="violationsCount === 0" class="ds-dossier-tile-note">нет нарушений</span>
            <span v-else-if="violationsCount === null" class="ds-dossier-tile-note">нет данных за месяц</span>
            <span v-else-if="breakdown" class="ds-dossier-tile-note">{{ breakdown }}</span>
        </div>

        <!-- Через биржу за месяц: пара worked_hours/worked_shifts одного exchange-summary по User. -->
        <div v-if="exchangeState.status === 'loading'" class="ds-dossier-skeleton" style="height: 86px" aria-busy="true"></div>
        <div v-else class="ds-dossier-tile is-exchange" title="По всем подразделениям">
            <span class="ds-dossier-tile-label">Через биржу</span>
            <span class="ds-dossier-tile-value" :class="{ 'is-muted': !exchangeMonth?.workedHours }">{{ exchangeMonth?.workedHours == null ? DASH : `${formatHours(exchangeMonth.workedHours)} ч` }}</span>
            <span v-if="exchangeState.status === 'forbidden'" class="ds-dossier-tile-note">Недоступно с текущими правами</span>
            <span v-else-if="exchangeState.status === 'error'" class="ds-dossier-tile-note">
                Ошибка · <button type="button" class="ds-dossier-link" @click="retryExchange">Повторить</button>
            </span>
            <span v-else-if="exchangeMonth?.workedShifts != null" class="ds-dossier-tile-note">{{ pluralShifts(exchangeMonth.workedShifts) }}</span>
        </div>
    </div>

    <!-- Полоса факт / план / норма -->
    <div v-if="statsState.status === 'ready'">
        <div class="ds-dossier-progress-head">
            <span>Факт <b>{{ formatHours(metrics.fact) }}</b> · план <b>{{ formatHours(metrics.plan) }}</b> · норма {{ formatHours(metrics.norm) }} ч</span>
            <span>{{ factPercent === null ? DASH : `${factPercent} % нормы` }}</span>
        </div>
        <div class="ds-dossier-progress" role="img" :aria-label="`Факт ${formatHours(metrics.fact)}, план ${formatHours(metrics.plan)}, норма ${formatHours(metrics.norm)} ч`">
            <div class="ds-dossier-progress-plan" :style="{ width: barWidth(metrics.plan) }"></div>
            <div class="ds-dossier-progress-fact" :style="{ width: barWidth(metrics.fact) }"></div>
        </div>
    </div>

    <!-- Полоса дней месяца -->
    <div>
        <DossierState :status="monthState.status" :height="22" error-text="Не удалось загрузить смены" @retry="retryMonth" />
        <template v-if="monthState.status === 'ready'">
            <div class="ds-dossier-days" :aria-label="`Дни: ${monthLabel}`">
                <span v-for="day in days" :key="day.key" class="ds-dossier-day" :class="`is-${day.kind}`" :style="day.color ? { background: day.color } : undefined" :title="day.title">{{ day.number }}</span>
            </div>
            <div class="ds-dossier-legend">
                <span v-for="item in legend" :key="item.key" class="ds-dossier-legend-item">
                    <span class="ds-dossier-legend-swatch ds-dossier-day" :class="`is-${item.kind}`" :style="item.color ? { background: item.color } : undefined"></span>{{ item.label }}
                </span>
            </div>
        </template>
    </div>

    <!-- Сейчас и дальше (только активные) -->
    <template v-if="!archive">
        <div class="ds-dossier-section-head"><span class="ds-dossier-section-title">Сейчас и дальше</span></div>
        <DossierState v-if="monthState.status !== 'ready'" :status="monthState.status" :height="60" error-text="Не удалось загрузить смены" @retry="retryMonth" />
        <div v-else class="ds-dossier-pairs">
            <span>Сегодня</span><span class="is-strong">{{ todayText }}</span>
            <span>Завтра</span><span>{{ tomorrowText }}</span>
        </div>
        <!-- Отпуск / больничный / командировка отображаемого ТУ с датами в ближайшие 3 месяца; иначе строки нет. -->
        <div v-if="absence || documentsState.status === 'error'" class="ds-dossier-pairs">
            <template v-if="absence">
                <span>{{ absence.label }}</span><span>{{ absence.text }}</span>
            </template>
            <template v-else>
                <span>Отсутствия</span>
                <span>Не удалось загрузить · <button type="button" class="ds-dossier-link" @click="data.documents.retry()">Повторить</button></span>
            </template>
        </div>
    </template>

    <!-- Трудоустройство -->
    <div class="ds-dossier-section-head">
        <span class="ds-dossier-section-title">Трудоустройство · {{ employment.departmentName || DASH }}</span>
    </div>
    <DossierState v-if="!employment.complete" :status="employmentStatus" :height="96" error-text="Не удалось загрузить трудоустройство" @retry="data.reloadUser()" />
    <div v-else class="ds-dossier-pairs">
        <span>Должность</span><span>{{ employment.positionName || DASH }}</span>
        <span>Ставка в час</span><span>{{ formatRub(employment.salaryHour) }}</span>
        <span>Доля / доступность</span><span>{{ formatPercentage(employment.percentage) }} · {{ shiftScheduleName }}</span>
        <span>Табельный код</span><span>{{ employment.tabelCode || DASH }}</span>
        <template v-if="employment.firedAt">
            <span>Дата окончания</span><span>{{ formatDate(employment.firedAt) }}</span>
        </template>
    </div>

    <!-- Только информация: без кликов, выбора ТУ и переключения досье (r2). -->
    <div v-if="others.length" class="ds-dossier-other">
        Ещё работает: {{ othersText }}
    </div>
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import { useScheduleStore } from '@/stores/scheduleStore'
import { dayStateColor, describeShiftTiming } from '@/components/schedule/weekSchedule'
import type { Shift } from '@/types'
import {
    type DossierEmployment,
    DASH,
    DATE_KEY,
    formatDate,
    formatHours,
    formatPercentage,
    formatRub,
    hasPlan,
    monthName,
    percentOfNorm,
    pickMetric,
    pluralShifts,
    shiftsByDate,
} from './dossierModel'
import { monthExchangeRange, monthParam, pickAbsence } from './dossierActivity'
import type { DossierData } from './useDossierData'
import DossierState from './DossierState.vue'

const props = defineProps<{
    data: DossierData
    employment: DossierEmployment
    others: DossierEmployment[]
    archive: boolean
    month: Dayjs
    today: Dayjs
    now: Dayjs
}>()

const scheduleStore = useScheduleStore()

// ---------- Метрики месяца (employee-statistics) ----------
const statsState = computed(() => props.data.readStats(props.employment.departmentId, props.month))
const metrics = computed(() => {
    const body = statsState.value.status === 'ready' ? statsState.value.data : null
    const id = props.employment.id
    return {
        fact: pickMetric(body, 'fact_hours', id),
        plan: pickMetric(body, 'plan_hours', id),
        norm: pickMetric(body, 'hours_norm', id),
    }
})
const factPercent = computed(() => percentOfNorm(metrics.value.fact, metrics.value.norm))
// Длина полосы ограничена контейнером; значение процента не подменяется.
const barWidth = (value: number | null) => {
    const percent = percentOfNorm(value, metrics.value.norm)
    return `${percent === null ? 0 : Math.max(0, Math.min(100, percent))}%`
}
const retryStats = () => props.data.retryStats(props.employment.departmentId, props.month)

// ---------- Нарушения месяца: GET /api/user/{id}/discipline/ (первая страница месяца) ----------
const disciplineQuery = computed(() => ({ from: monthParam(props.month), to: monthParam(props.month), page: 1 }))
const disciplineState = computed(() => props.data.discipline.read(disciplineQuery.value))
const violationsCount = computed(() => disciplineState.value.data?.totals.violationsCount ?? null)
const retryDiscipline = () => props.data.discipline.retry(disciplineQuery.value)
// Разбивка из totals того же ответа; violations_count не равен сумме частей (прогулы, одна смена — оба вида).
const breakdown = computed(() => {
    const totals = disciplineState.value.data?.totals
    if (!totals) return null
    const parts = [
        totals.lateCount ? `${totals.lateCount} опозд.` : '',
        totals.earlyCount ? `${totals.earlyCount} уход` : '',
        totals.noMarksDays ? `${totals.noMarksDays} без отм.` : '',
        totals.absenceDays ? `${totals.absenceDays} прогул` : '',
    ].filter(Boolean)
    return parts.join(' · ') || null
})

// ---------- Через биржу за месяц: exchange-summary с границами месяца ----------
const exchangeQuery = computed(() => monthExchangeRange(props.month))
const exchangeState = computed(() => props.data.exchangeSummary.read(exchangeQuery.value))
const exchangeMonth = computed(() => exchangeState.value.data)
const retryExchange = () => props.data.exchangeSummary.retry(exchangeQuery.value)

// ---------- Смены месяца ----------
const monthRange = computed(() => {
    const start = props.month.startOf('month')
    let end = props.month.endOf('month')
    // «Завтра» в последний день месяца — в том же запросе.
    const tomorrow = props.today.add(1, 'day')
    if (!props.archive && tomorrow.isAfter(end, 'day')) end = tomorrow
    return { start: start.format(DATE_KEY), end: end.format(DATE_KEY) }
})
const monthState = computed(() => props.data.readSchedule(props.employment.id, monthRange.value.start, monthRange.value.end))
const retryMonth = () => props.data.retrySchedule(props.employment.id, monthRange.value.start, monthRange.value.end)
const monthLabel = computed(() => `${monthName(props.month)} ${props.month.year()}`)

watchEffect(() => {
    const { id, departmentId } = props.employment
    props.data.ensureStats(departmentId, props.month)
    props.data.ensureSchedule(id, monthRange.value.start, monthRange.value.end)
    props.data.discipline.ensure(disciplineQuery.value)
    props.data.exchangeSummary.ensure(exchangeQuery.value)
    if (!props.archive) props.data.documents.ensure()
})

const byDate = computed(() => shiftsByDate(monthState.value.data?.shifts ?? []))
const todayKey = computed(() => props.today.format(DATE_KEY))

type DayKind = 'ok' | 'violation' | 'no-marks' | 'day-state' | 'planned' | 'empty' | 'today'
const dayTypeOf = (shift: Shift) => scheduleStore.dayTypes.find(item => item.id === shift.day_type)
const dayTypeName = (shift: Shift) => dayTypeOf(shift)?.name

// Статусы по данным графика и прежней логике нарушений main-front (describeShiftTiming):
// идущая смена — не «нет отметок»; новые правила дисциплины по разнице времени не вводятся.
interface DayClass { kind: DayKind, label: string, dayTypeId?: string, color?: string }

function classify(key: string, shifts: Shift[]): DayClass {
    const planned = shifts.filter(hasPlan)
    const dayState = shifts.find(shift => shift.day_state_document)
    let result: DayClass
    if (!planned.length) {
        // Кадровое состояние: вид — реальный DayType смены (day_type → справочник: отпуск, больничный, …),
        // цвет — DayType.color, как в графике (dayStateColor). Не выводится из UUID документа.
        if (dayState) {
            const dayType = dayTypeOf(dayState)
            result = { kind: 'day-state', label: dayType?.name ?? 'кадровое состояние', dayTypeId: dayType?.id ?? 'unknown', color: dayStateColor(dayType?.color) }
        } else result = { kind: 'empty', label: 'нет смены' }
    } else if (key > todayKey.value) {
        result = { kind: 'planned', label: 'запланирована' }
    } else {
        const timings = planned.map(shift => describeShiftTiming(shift, props.now, undefined))
        if (timings.some(item => item.isInProgress || item.isUpcoming)) result = { kind: 'planned', label: 'идёт' }
        else if (timings.some(item => item.isNoMarks)) result = { kind: 'no-marks', label: 'нет отметок' }
        else if (timings.some(item => item.violationCount > 0 || item.hasGeneralViolation)) result = { kind: 'violation', label: 'нарушение' }
        else result = { kind: 'ok', label: 'смена без нарушений' }
    }
    if (key === todayKey.value && !props.archive) return { kind: 'today', label: `сегодня · ${result.label}` }
    return result
}

const days = computed(() => {
    const start = props.month.startOf('month')
    return Array.from({ length: start.daysInMonth() }, (_, index) => {
        const date = start.add(index, 'day')
        const key = date.format(DATE_KEY)
        const { kind, label, dayTypeId, color } = classify(key, byDate.value.get(key) ?? [])
        return { key, number: date.date(), kind, label, dayTypeId, color, title: `${date.format('D MMMM')} · ${label}` }
    })
})

// Легенда: статусы смен + фактически встретившиеся в месяце кадровые состояния с их названием и цветом.
const legend = computed(() => {
    const items: { key: string, kind: DayKind, label: string, color?: string }[] = [
        { key: 'ok', kind: 'ok', label: 'смена без нарушений' },
        { key: 'violation', kind: 'violation', label: 'нарушение' },
        { key: 'no-marks', kind: 'no-marks', label: 'нет отметок' },
    ]
    const seen = new Set<string>()
    for (const day of days.value) {
        if (day.kind !== 'day-state' || !day.dayTypeId || seen.has(day.dayTypeId)) continue
        seen.add(day.dayTypeId)
        items.push({ key: `state-${day.dayTypeId}`, kind: 'day-state', label: day.label, color: day.color })
    }
    items.push({ key: 'planned', kind: 'planned', label: 'запланирована' })
    return items
})

// ---------- Сейчас и дальше ----------
function describeDay(date: Dayjs) {
    const shifts = byDate.value.get(date.format(DATE_KEY)) ?? []
    const planned = shifts.filter(hasPlan)
    if (!planned.length) {
        const dayState = shifts.find(shift => shift.day_state_document || shift.day_type)
        return dayState ? (dayTypeName(dayState) ?? 'Смены нет') : 'Смены нет'
    }
    return planned.map((shift) => {
        const timing = describeShiftTiming(shift, props.now, undefined)
        const plan = `${timing.planStart}–${timing.planEnd}`
        if (timing.isInProgress) return `${plan} · идёт с ${timing.factStart}`
        if (timing.isNoMarks) return `${plan} · нет отметок`
        if (!timing.isUpcoming && timing.factStart !== '—' && timing.factEnd !== '—') return `${plan} · ${timing.factStart}–${timing.factEnd}`
        return plan
    }).join(', ')
}
const todayText = computed(() => describeDay(props.today))
const tomorrowText = computed(() => describeDay(props.today.add(1, 'day')))

// ---------- Отсутствие: day-state-documents ----------
const documentsState = computed(() => props.data.documents.read())
const absence = computed(() => {
    if (documentsState.value.status !== 'ready') return null
    const picked = pickAbsence(documentsState.value.data ?? [], props.employment.id, props.today)
    if (!picked) return null
    const start = dayjs(picked.document.dateFrom!)
    const end = dayjs(picked.document.dateTo!)
    const range = start.isSame(end, 'month') ? `${start.format('D')}–${end.format('D MMMM')}` : `${start.format('D MMMM')} – ${end.format('D MMMM')}`
    return { label: picked.document.dayTypeName!, text: picked.current ? `${range} (идёт)` : range }
})

// ---------- Трудоустройство ----------
const employmentStatus = computed(() => {
    const status = props.data.userStatus.value
    return status === 'ready' ? 'error' : status
})
const othersText = computed(() => props.others
    .map(item => `${item.departmentName || DASH}, ${item.positionName || DASH}${item.percentage === null ? '' : ` (${formatPercentage(item.percentage)})`}`)
    .join('; '))
const shiftScheduleName = computed(() => (
    scheduleStore.shiftSchedules.find((item: { id: string }) => item.id === props.employment.shiftScheduleId)?.name ?? DASH
))
</script>
